import React, { useEffect, useState, createContext, useContext } from 'react';
type Role = 'admin' | 'user';
interface User {
  id: string;
  username: string;
  role: Role;
  name: string;
}
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Mock users for demonstration
const MOCK_USERS = [{
  id: '1',
  username: 'admin',
  password: 'admin123',
  role: 'admin' as Role,
  name: 'Admin User'
}, {
  id: '2',
  username: 'user',
  password: 'user123',
  role: 'user' as Role,
  name: 'Regular User'
}];
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  useEffect(() => {
    // Check for saved user in localStorage on initial load
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setIsAuthenticated(true);
    }
  }, []);
  const login = async (username: string, password: string): Promise<boolean> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    const foundUser = MOCK_USERS.find(u => u.username === username && u.password === password);
    if (foundUser) {
      const {
        password,
        ...userWithoutPassword
      } = foundUser;
      setUser(userWithoutPassword);
      setIsAuthenticated(true);
      localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
      return true;
    }
    return false;
  };
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('currentUser');
  };
  const isAdmin = user?.role === 'admin';
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    isAuthenticated,
    isAdmin
  }} data-id="element-457">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};